import { type FC } from "react";

import classNames from "classnames";

import { Badge } from "@jti/core-components/components/Badge";
import { Button } from "@jti/core-components/components/Button";
import { ImageBehaviour } from "@jti/core-components/components/ImageBehaviour";
import { LinkBehaviour } from "@jti/core-components/components/LinkBehaviour";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { VideoPlayer } from "../VideoPlayer";

import styles from "./HighlightCard.module.scss";

export type HighlightCardProps = {
  title: string;
  description?: string;
  imageUrl: string;
  rightBadgeText?: string;
  leftBadgeText?: string;
  buttonText: string;
  linkUrl: string;
  theme: "dark" | "light";
  alt?: string;
  imageTitle?: string;
  className?: string;
  backgroundType?: "video" | "image";
  videoFileReference?: string;
  trackingEvent?: string;
  openIn?: "newTab" | "sameTab";
};

const HIGHLIGHT_CARD_THEME = {
  dark: "dark-theme",
  light: "light-theme",
} as const;

export const HighlightCard: FC<HighlightCardProps> = ({
  title,
  description,
  imageUrl,
  rightBadgeText,
  leftBadgeText,
  theme,
  buttonText,
  linkUrl,
  alt = "",
  imageTitle,
  className,
  backgroundType = "image",
  videoFileReference,
  trackingEvent,
  openIn = "sameTab",
}) => {
  const dispatchGTMEvent = () => {
    const ctaName = trackingEvent === "cta_click" ? title : trackingEvent;

    dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
      cta_name: ctaName || title,
      cta_location: "highlight-card",
    });
  };

  return (
    <LinkBehaviour
      href={linkUrl}
      className={classNames(styles.highlightCard, HIGHLIGHT_CARD_THEME[theme], className)}
      target={openIn === "newTab" ? "_blank" : undefined}
    >
      <div className={styles.topBar}>
        {leftBadgeText ? <Badge variant="ghost" label={leftBadgeText} data-testid="leftBadge" /> : <span />}

        {rightBadgeText && <Badge label={rightBadgeText} data-testid="rightBadge" />}
      </div>

      <div className={styles.bottomSection}>
        <h3 className={styles.title}>{title}</h3>
        {description && <p className={styles.description}>{description}</p>}
        <Button variant="secondary" size="sm" className={styles.button} tabIndex={-1} onClick={dispatchGTMEvent}>
          {buttonText}
        </Button>
      </div>

      <div
        className={classNames(styles.gradientOverlay, { [styles.onlyBottom]: !leftBadgeText && !rightBadgeText })}
      ></div>

      {backgroundType === "image" && (
        <ImageBehaviour src={imageUrl} className={styles.backgroundImage} alt={alt || title} title={imageTitle} />
      )}

      {backgroundType === "video" && videoFileReference && (
        <div className={styles.backgroundVideo}>
          <VideoPlayer
            className={styles.videoPlayerWrapper}
            desktopSrc={videoFileReference}
            showControls={false}
            loop={true}
            autoPlay={true}
            preventMainPlayButtonClick={true}
            videoClassName={styles.videoPlayer}
          />
        </div>
      )}
    </LinkBehaviour>
  );
};
